import { Navigate } from "react-router-dom";
import { useEffect, type ReactNode } from "react";

import type { AccountRole } from "@cwm/shared";

import { useAuth } from "@/lib/auth";
import { useOptionalToast } from "@/components/ui/Toast";

/**
 * Route guard for account-scoped role checks. Renders children when the
 * caller holds `role` in the active account OR is a platform super admin.
 *
 * When the check fails we redirect to `/` and surface a toast — the gate is
 * a defense-in-depth UX hint; the API is the source of truth on authorization.
 */
export interface RequireAccountRoleProps {
  role: AccountRole;
  children: ReactNode;
}

export function RequireAccountRole({
  role,
  children,
}: RequireAccountRoleProps) {
  const { hasAccountRole, loading } = useAuth();
  const { toast } = useOptionalToast();
  const allowed = !loading && hasAccountRole(role);

  useEffect(() => {
    if (!loading && !allowed) {
      toast({
        title: "Not authorized",
        description: "You don't have permission to view that page.",
        variant: "warn",
      });
    }
  }, [allowed, loading, toast]);

  if (loading) {
    // Let the parent <ProtectedRoute> handle spinner; avoid double-flash here.
    return null;
  }

  if (!allowed) {
    return <Navigate to="/dashboard" replace />;
  }

  return <>{children}</>;
}
